import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Wallet, ChevronDown, ChevronLeft, ChevronRight, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { generatePaystubPdf, generatePaystubsSummaryPdf } from "@/utils/reports.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import { PdfPreviewDialog } from "@/components/reports/PdfPreviewDialog";

function startOfWeek(date: Date) {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfWeek(date: Date) {
  const s = startOfWeek(date);
  const e = new Date(s);
  e.setDate(s.getDate() + 7);
  return e;
}

export const Route = createFileRoute("/_app/payroll/my-paystubs")({
  component: MyPaystubsPage,
});

type RunStatus = "draft" | "pending_employee_approval" | "finalized" | "paid" | "cancelled";

type Paystub = {
  id: string;
  payroll_run_id: string;
  hours: number;
  hourly_rate: number;
  gross_pay: number;
  entries_count: number;
  notes: string | null;
  payroll_runs: {
    id: string;
    number: string;
    name: string | null;
    period_start: string;
    period_end: string;
    pay_date: string | null;
    status: RunStatus;
    currency: string;
  };
};

type DayEntry = {
  day: string; // YYYY-MM-DD
  clockIn: string; // ISO
  clockOut: string | null; // ISO or null
  breakMinutes: number;
  hours: number;
  amount: number;
};

type SiteGroup = {
  key: string; // unique key (job_site_id|project_id|"unassigned")
  name: string;
  hours: number;
  amount: number;
  days: DayEntry[];
};

function MyPaystubsPage() {
  const { t, i18n } = useTranslation();
  const { profile, company, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [paystubs, setPaystubs] = useState<
    (Paystub & { user_id: string; employee_name: string | null })[]
  >([]);
  const [siteGroups, setSiteGroups] = useState<Record<string, SiteGroup[]>>({});
  const [loading, setLoading] = useState(true);
  const [pdfExporting, setPdfExporting] = useState<string | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState("");

  const paystubPdfFn = useServerFn(generatePaystubPdf);
  const summaryPdfFn = useServerFn(generatePaystubsSummaryPdf);
  const [summaryExporting, setSummaryExporting] = useState(false);

  const COLLAPSED_KEY = "myPaystubs:collapsedCards";
  // Default: recolhido. Estado guarda quais cards estão EXPANDIDOS.
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLLAPSED_KEY);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(expandedCards));
    } catch {
      // ignore
    }
  }, [expandedCards]);

  const toggleCard = (key: string) => setExpandedCards((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleExportPaystubPdf = async (
    cardKey: string,
    employeeName: string,
    itemIds: string[],
    periodStart: string,
    periodEnd: string,
    isPaid?: boolean,
    payDate?: string | null,
  ) => {
    setPdfExporting(cardKey);
    try {
      const { pdfBase64, filename } = await paystubPdfFn({
        data: {
          payrollRunItemIds: itemIds,
          periodStart,
          periodEnd,
          employeeName,
          isPaid: isPaid ?? false,
          payDate: payDate ?? null,
        },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao gerar PDF");
    } finally {
      setPdfExporting(null);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(pdfPreviewFilename, pdfPreviewBase64);
    setPdfPreviewOpen(false);
  };

  const load = async () => {
    if (!profile?.id) return;
    setLoading(true);
    let query = supabase
      .from("payroll_run_items")
      .select(
        `id, payroll_run_id, user_id, employee_name, hours, hourly_rate, gross_pay, entries_count, notes,
         payroll_runs!inner(id, number, name, period_start, period_end, pay_date, status, currency, company_id)`,
      )
      .order("created_at", { ascending: false });
    if (isAdmin && company?.id) {
      query = query.eq("payroll_runs.company_id", company.id);
    } else {
      query = query.eq("user_id", profile.id);
    }
    const { data, error } = await query;
    if (error) toast.error(error.message);
    const items = (data ?? []) as unknown as (Paystub & {
      user_id: string;
      employee_name: string | null;
    })[];
    setPaystubs(items);

    // Fetch the days worked for each paystub via entry links → time_entries
    const itemIds = items.map((p) => p.id);
    if (itemIds.length > 0) {
      const { data: links, error: linksError } = await supabase
        .from("payroll_run_entry_links")
        .select(
          `payroll_run_item_id, hours, amount, job_site_id, project_id,
           time_entries(clock_in, clock_out, break_minutes),
           job_sites(name),
           projects(name)`,
        )
        .in("payroll_run_item_id", itemIds);
      if (linksError) {
        toast.error(linksError.message);
      } else {
        // Group: itemId -> siteKey -> { name, days(Map<day, DayEntry>), hours, amount }
        const grouped: Record<
          string,
          Map<string, { name: string; hours: number; amount: number; days: Map<string, DayEntry> }>
        > = {};

        for (const link of (links ?? []) as unknown as Array<{
          payroll_run_item_id: string;
          hours: number;
          amount: number;
          job_site_id: string | null;
          project_id: string | null;
          time_entries: {
            clock_in: string;
            clock_out: string | null;
            break_minutes: number | null;
          } | null;
          job_sites: { name: string } | null;
          projects: { name: string } | null;
        }>) {
          const siteKey = link.job_site_id ?? link.project_id ?? "unassigned";
          const siteName =
            link.job_sites?.name ??
            link.projects?.name ??
            t("pages.myPaystubs.unassignedSite", { defaultValue: "Sem obra" });

          if (!grouped[link.payroll_run_item_id]) {
            grouped[link.payroll_run_item_id] = new Map();
          }
          const itemSites = grouped[link.payroll_run_item_id];
          let bucket = itemSites.get(siteKey);
          if (!bucket) {
            bucket = { name: siteName, hours: 0, amount: 0, days: new Map() };
            itemSites.set(siteKey, bucket);
          }
          bucket.hours += Number(link.hours) || 0;
          bucket.amount += Number(link.amount) || 0;

          const ci = link.time_entries?.clock_in;
          if (!ci) continue;
          const day = ci.slice(0, 10);
          const co = link.time_entries?.clock_out ?? null;
          const bm = Number(link.time_entries?.break_minutes) || 0;
          const existing = bucket.days.get(day);
          if (!existing) {
            bucket.days.set(day, {
              day,
              clockIn: ci,
              clockOut: co,
              breakMinutes: bm,
              hours: Number(link.hours) || 0,
              amount: Number(link.amount) || 0,
            });
          } else {
            const newCi = ci < existing.clockIn ? ci : existing.clockIn;
            const newCo = !existing.clockOut
              ? co
              : !co
                ? existing.clockOut
                : co > existing.clockOut
                  ? co
                  : existing.clockOut;
            bucket.days.set(day, {
              day,
              clockIn: newCi,
              clockOut: newCo,
              breakMinutes: existing.breakMinutes + bm,
              hours: existing.hours + (Number(link.hours) || 0),
              amount: existing.amount + (Number(link.amount) || 0),
            });
          }
        }

        const result: Record<string, SiteGroup[]> = {};
        for (const [itemId, sites] of Object.entries(grouped)) {
          result[itemId] = Array.from(sites.entries())
            .map(([key, b]) => ({
              key,
              name: b.name,
              hours: b.hours,
              amount: b.amount,
              days: Array.from(b.days.values()).sort((a, b) => a.day.localeCompare(b.day)),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        }
        setSiteGroups(result);
      }
    } else {
      setSiteGroups({});
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const fm = (v: number, cur: string) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(v);

  const fmtDay = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1).toLocaleDateString(i18n.language, {
      day: "2-digit",
      month: "short",
    });
  };

  const fmtTime = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: company?.timezone || undefined,
    });
  };

  // Modo de filtro: semana ou mês
  const [filterMode, setFilterMode] = useState<"week" | "month">("week");

  // Navegação por semana (igual ao lançamento de ponto)
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const weekEnd = endOfWeek(weekStart);

  // Navegação por mês (primeiro dia do mês selecionado)
  const [monthStart, setMonthStart] = useState<Date>(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);

  const toIsoDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const fmtRange = (s: Date, e: Date) => {
    const eVisible = new Date(e);
    eVisible.setDate(e.getDate() - 1);
    return `${s.toLocaleDateString(i18n.language, { month: "short", day: "numeric" })} – ${eVisible.toLocaleDateString(i18n.language, { month: "short", day: "numeric", year: "numeric" })}`;
  };

  const fmtMonth = (d: Date) =>
    d.toLocaleDateString(i18n.language, { month: "long", year: "numeric" });

  // Show only non-draft, non-cancelled paystubs (anything the admin issued)
  const nonDraft = paystubs.filter(
    (p) => p.payroll_runs.status !== "draft" && p.payroll_runs.status !== "cancelled",
  );

  // Holerite incluído se seu intervalo [period_start, period_end] sobrepõe o intervalo selecionado
  const activeStart = filterMode === "week" ? weekStart : monthStart;
  const activeEnd = filterMode === "week" ? weekEnd : monthEnd;
  const rangeStart = toIsoDate(activeStart);
  const rangeEnd = toIsoDate(new Date(activeEnd.getTime() - 1));
  const visible = nonDraft.filter((p) => {
    const ps = p.payroll_runs.period_start;
    const pe = p.payroll_runs.period_end;
    return ps <= rangeEnd && pe >= rangeStart;
  });

  type Consolidated = {
    sites: SiteGroup[];
    totalHours: number;
    totalGross: number;
    totalEntries: number;
    currency: string;
    periodStart: string;
    periodEnd: string;
    runCount: number;
    itemIds: string[];
    runs: Array<{
      id: string;
      number: string;
      periodStart: string;
      periodEnd: string;
      payDate: string | null;
      status: RunStatus;
    }>;
    statusCounts: Record<string, number>;
    employeeName: string | null;
  };

  const consolidate = (
    items: (Paystub & { user_id: string; employee_name: string | null })[],
  ): Consolidated | null => {
    if (items.length === 0) return null;
    const sitesMap = new Map<
      string,
      { name: string; hours: number; amount: number; days: Map<string, DayEntry> }
    >();
    let totalHours = 0;
    let totalGross = 0;
    let totalEntries = 0;
    const currency = items[0]?.payroll_runs.currency ?? "USD";

    for (const p of items) {
      const groups = siteGroups[p.id] ?? [];
      for (const g of groups) {
        // Only include days within the active range (rangeStart..rangeEnd)
        const daysInRange = g.days.filter((d) => d.day >= rangeStart && d.day <= rangeEnd);
        if (daysInRange.length === 0) continue;

        let bucket = sitesMap.get(g.key);
        if (!bucket) {
          bucket = { name: g.name, hours: 0, amount: 0, days: new Map() };
          sitesMap.set(g.key, bucket);
        }
        for (const d of daysInRange) {
          bucket.hours += d.hours;
          bucket.amount += d.amount;
          totalHours += d.hours;
          totalGross += d.amount;
          totalEntries += 1;
          const existing = bucket.days.get(d.day);
          if (!existing) {
            bucket.days.set(d.day, { ...d });
          } else {
            const newCi = d.clockIn < existing.clockIn ? d.clockIn : existing.clockIn;
            const newCo = !existing.clockOut
              ? d.clockOut
              : !d.clockOut
                ? existing.clockOut
                : d.clockOut > existing.clockOut
                  ? d.clockOut
                  : existing.clockOut;
            bucket.days.set(d.day, {
              day: d.day,
              clockIn: newCi,
              clockOut: newCo,
              breakMinutes: existing.breakMinutes + d.breakMinutes,
              hours: existing.hours + d.hours,
              amount: existing.amount + d.amount,
            });
          }
        }
      }
    }

    if (sitesMap.size === 0) return null;

    const sites: SiteGroup[] = Array.from(sitesMap.entries())
      .map(([key, b]) => ({
        key,
        name: b.name,
        hours: b.hours,
        amount: b.amount,
        days: Array.from(b.days.values()).sort((a, b) => a.day.localeCompare(b.day)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const runs = items
      .map((p) => ({
        id: p.payroll_runs.id,
        number: p.payroll_runs.number,
        periodStart: p.payroll_runs.period_start,
        periodEnd: p.payroll_runs.period_end,
        payDate: p.payroll_runs.pay_date,
        status: p.payroll_runs.status,
      }))
      .sort((a, b) => a.periodStart.localeCompare(b.periodStart));

    const periodStart =
      runs.map((r) => r.periodStart).sort()[0] ?? items[0]?.payroll_runs.period_start;
    const periodEnd =
      runs
        .map((r) => r.periodEnd)
        .sort()
        .slice(-1)[0] ?? items[0]?.payroll_runs.period_end;

    const statusCounts = runs.reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
      return acc;
    }, {});

    return {
      sites,
      totalHours,
      totalGross,
      totalEntries,
      currency,
      periodStart,
      periodEnd,
      runCount: items.length,
      itemIds: items.map((i) => i.id),
      runs,
      statusCounts,
      employeeName: items[0]?.employee_name ?? null,
    };
  };

  // For admins: group by user_id (one card per employee).
  // For employees: a single group containing all their own paystubs.
  const employeeCards: Array<{ userId: string; name: string; data: Consolidated }> = (() => {
    if (visible.length === 0) return [];
    if (!isAdmin) {
      const data = consolidate(visible);
      if (!data) return [];
      return [
        {
          userId: profile?.id ?? "self",
          name: profile?.full_name ?? data.employeeName ?? t("pages.myPaystubs.title"),
          data,
        },
      ];
    }
    const byUser = new Map<
      string,
      (Paystub & { user_id: string; employee_name: string | null })[]
    >();
    for (const p of visible) {
      if (!byUser.has(p.user_id)) byUser.set(p.user_id, []);
      byUser.get(p.user_id)!.push(p);
    }
    const cards: Array<{ userId: string; name: string; data: Consolidated }> = [];
    for (const [uid, items] of byUser.entries()) {
      const data = consolidate(items);
      if (!data) continue;
      cards.push({
        userId: uid,
        name: items[0]?.employee_name ?? t("pages.myPaystubs.title"),
        data,
      });
    }
    cards.sort((a, b) => a.name.localeCompare(b.name));
    return cards;
  })();

  const hasAny = employeeCards.length > 0;

  const handleExportSummaryPdf = async () => {
    if (!hasAny) return;
    setSummaryExporting(true);
    try {
      const periodLabel =
        filterMode === "week" ? fmtRange(weekStart, weekEnd) : fmtMonth(monthStart);
      const cards = employeeCards.map(({ name, data }) => ({
        name,
        periodStart: data.periodStart,
        periodEnd: data.periodEnd,
        runCount: data.runCount,
        totalEntries: data.totalEntries,
        totalHours: data.totalHours,
        totalGross: data.totalGross,
        currency: data.currency,
        statuses: Object.entries(data.statusCounts).map(([status, count]) => ({
          label: t(`pages.myPaystubs.runStatus.${status}`),
          count: count as number,
        })),
      }));
      const { pdfBase64, filename } = await summaryPdfFn({
        data: {
          periodStart: rangeStart,
          periodEnd: rangeEnd,
          periodLabel,
          cards,
        },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao gerar PDF");
    } finally {
      setSummaryExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("pages.myPaystubs.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("pages.myPaystubs.subtitleReadOnly", {
              defaultValue: "Review your issued paystubs and payment status.",
            })}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {filterMode === "week" ? fmtRange(weekStart, weekEnd) : fmtMonth(monthStart)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
            <Button
              variant={filterMode === "week" ? "default" : "ghost"}
              size="sm"
              className="h-8"
              onClick={() => setFilterMode("week")}
            >
              {t("pages.myPaystubs.modeWeek", { defaultValue: "Semana" })}
            </Button>
            <Button
              variant={filterMode === "month" ? "default" : "ghost"}
              size="sm"
              className="h-8"
              onClick={() => setFilterMode("month")}
            >
              {t("pages.myPaystubs.modeMonth", { defaultValue: "Mês" })}
            </Button>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (filterMode === "week") {
                const d = new Date(weekStart);
                d.setDate(d.getDate() - 7);
                setWeekStart(d);
              } else {
                setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1));
              }
            }}
            aria-label={
              filterMode === "week"
                ? t("pages.myPaystubs.prevWeek", { defaultValue: "Semana anterior" })
                : t("pages.myPaystubs.prevMonth", { defaultValue: "Mês anterior" })
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (filterMode === "week") {
                setWeekStart(startOfWeek(new Date()));
              } else {
                const n = new Date();
                setMonthStart(new Date(n.getFullYear(), n.getMonth(), 1));
              }
            }}
          >
            {filterMode === "week"
              ? t("pages.myPaystubs.thisWeek", { defaultValue: "Esta semana" })
              : t("pages.myPaystubs.thisMonth", { defaultValue: "Este mês" })}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              if (filterMode === "week") {
                const d = new Date(weekStart);
                d.setDate(d.getDate() + 7);
                setWeekStart(d);
              } else {
                setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1));
              }
            }}
            aria-label={
              filterMode === "week"
                ? t("pages.myPaystubs.nextWeek", { defaultValue: "Próxima semana" })
                : t("pages.myPaystubs.nextMonth", { defaultValue: "Próximo mês" })
            }
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasAny || summaryExporting}
            onClick={handleExportSummaryPdf}
          >
            {summaryExporting ? (
              <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-1.5 h-4 w-4" />
            )}
            {t("pages.myPaystubs.exportSummaryPdf", { defaultValue: "Gerar PDF" })}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {t("pages.myPaystubs.loading")}
        </p>
      ) : !hasAny ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Wallet className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">{t("pages.myPaystubs.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {employeeCards.map(({ userId, name, data }) => {
            const cardKey = String(userId ?? name);
            const isExpanded = expandedCards[cardKey] ?? false;
            return (
              <Card key={cardKey}>
                <CardHeader
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCard(cardKey)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleCard(cardKey);
                    }
                  }}
                  aria-expanded={isExpanded}
                  className="cursor-pointer transition-colors hover:bg-muted/40"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <ChevronDown
                        className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? "" : "-rotate-90"
                        }`}
                      />
                      <div>
                        <CardTitle className="text-lg">{name}</CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {new Date(data.periodStart).toLocaleDateString(undefined, {
                            timeZone: "UTC",
                          })}{" "}
                          –{" "}
                          {new Date(data.periodEnd).toLocaleDateString(undefined, {
                            timeZone: "UTC",
                          })}
                          {" · "}
                          {t("pages.myPaystubs.runsCount", {
                            defaultValue: "{{count}} holerites",
                            count: data.runCount,
                          })}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span>
                            <span className="font-medium text-foreground tabular-nums">
                              {data.totalEntries}
                            </span>{" "}
                            {t("pages.myPaystubs.statEntries")}
                          </span>
                          <span className="text-muted-foreground/40">·</span>
                          <span>
                            <span className="font-medium text-foreground tabular-nums">
                              {data.totalHours.toFixed(2)}
                            </span>{" "}
                            {t("pages.myPaystubs.statHours")}
                          </span>
                          <span className="text-muted-foreground/40">·</span>
                          <span>
                            <span className="font-semibold text-foreground tabular-nums">
                              {fm(data.totalGross, data.currency)}
                            </span>{" "}
                            {t("pages.myPaystubs.statGross")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {data.runCount === 1 ? (
                        <RunBadge status={data.runs[0].status} />
                      ) : (
                        Object.entries(data.statusCounts).map(([status, count]) => (
                          <RunBadge
                            key={status}
                            status={status as RunStatus}
                            count={count as number}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </CardHeader>
                {isExpanded ? (
                  <CardContent className="space-y-6">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <Stat
                        label={t("pages.myPaystubs.statEntries")}
                        value={String(data.totalEntries)}
                      />
                      <Stat
                        label={t("pages.myPaystubs.statHours")}
                        value={data.totalHours.toFixed(2)}
                      />
                      <Stat
                        label={t("pages.myPaystubs.statGross")}
                        value={fm(data.totalGross, data.currency)}
                        highlight
                      />
                    </div>
                    <div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pdfExporting === cardKey}
                        onClick={(e) => {
                          e.stopPropagation();
                          const allPaid = data.runs.every((r) => r.status === "paid");
                          const latestPayDate =
                            data.runs
                              .map((r) => r.payDate)
                              .filter(Boolean)
                              .sort()
                              .pop() ?? null;
                          handleExportPaystubPdf(
                            cardKey,
                            name,
                            data.itemIds,
                            data.periodStart,
                            data.periodEnd,
                            allPaid,
                            latestPayDate,
                          );
                        }}
                      >
                        {pdfExporting === cardKey ? (
                          <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                          <FileText className="mr-1.5 h-4 w-4" />
                        )}
                        {t("pages.myPaystubs.exportPdf", { defaultValue: "Gerar PDF" })}
                      </Button>
                    </div>
                    {data.runCount > 1 && (
                      <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t("pages.myPaystubs.runsBreakdown", {
                            defaultValue: "Períodos incluídos",
                          })}
                        </p>
                        <ul className="space-y-1.5">
                          {data.runs.map((r) => (
                            <li
                              key={r.id}
                              className="flex flex-wrap items-center justify-between gap-2 text-sm"
                            >
                              <span className="text-muted-foreground">
                                <span className="font-medium text-foreground">{r.number}</span>
                                {" · "}
                                {new Date(r.periodStart).toLocaleDateString(undefined, {
                                  timeZone: "UTC",
                                })}{" "}
                                –{" "}
                                {new Date(r.periodEnd).toLocaleDateString(undefined, {
                                  timeZone: "UTC",
                                })}
                              </span>
                              <RunBadge status={r.status} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {data.sites.length > 0 &&
                      (() => {
                        const allRows = data.sites
                          .flatMap((group) =>
                            group.days.map((d) => ({
                              ...d,
                              siteKey: group.key,
                              siteName: group.name,
                            })),
                          )
                          .sort(
                            (a, b) =>
                              a.day.localeCompare(b.day) || a.siteName.localeCompare(b.siteName),
                          );
                        return (
                          <div className="overflow-x-auto rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>
                                    {t("pages.myPaystubs.colDate", { defaultValue: "Date" })}
                                  </TableHead>
                                  <TableHead>
                                    {t("pages.myPaystubs.colSite", { defaultValue: "Obra" })}
                                  </TableHead>
                                  <TableHead>
                                    {t("pages.myPaystubs.colClockIn", { defaultValue: "Clock in" })}
                                  </TableHead>
                                  <TableHead>
                                    {t("pages.myPaystubs.colClockOut", {
                                      defaultValue: "Clock out",
                                    })}
                                  </TableHead>
                                  <TableHead className="text-right">
                                    {t("pages.myPaystubs.colBreak", { defaultValue: "Intervalo" })}
                                  </TableHead>
                                  <TableHead className="text-right">
                                    {t("pages.myPaystubs.statHours")}
                                  </TableHead>
                                  <TableHead className="text-right">
                                    {t("pages.myPaystubs.colPayment", { defaultValue: "Payment" })}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {allRows.map((d, idx) => (
                                  <TableRow key={`${d.siteKey}-${d.day}-${idx}`}>
                                    <TableCell className="font-medium">{fmtDay(d.day)}</TableCell>
                                    <TableCell className="text-muted-foreground">
                                      {d.siteName}
                                    </TableCell>
                                    <TableCell>{fmtTime(d.clockIn)}</TableCell>
                                    <TableCell>{fmtTime(d.clockOut)}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {d.breakMinutes > 0
                                        ? `${d.breakMinutes} ${t("pages.myPaystubs.minutesShort", { defaultValue: "min" })}`
                                        : "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {d.hours.toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-right font-medium tabular-nums">
                                      {fm(d.amount, data.currency)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        );
                      })()}
                  </CardContent>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        <Link to="/payroll" className="hover:text-foreground hover:underline">
          {t("pages.myPaystubs.goAllRuns")}
        </Link>
      </p>

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        pdfBase64={pdfPreviewBase64}
        filename={pdfPreviewFilename}
        onDownload={confirmDownloadPdf}
      />
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={highlight ? "text-lg font-semibold" : "text-base font-medium"}>{value}</p>
    </div>
  );
}

function RunBadge({ status, count }: { status: RunStatus; count?: number }) {
  const { t } = useTranslation();
  const map: Record<RunStatus, string> = {
    draft: "border-muted-foreground/40 text-muted-foreground",
    pending_employee_approval: "border-primary/40 text-primary",
    finalized: "border-primary/40 text-primary",
    paid: "border-primary/40 bg-primary/10 text-primary",
    cancelled: "border-destructive/40 text-destructive",
  };
  return (
    <Badge variant="outline" className={map[status]}>
      {count !== undefined && count > 1 ? `${count}× ` : ""}
      {t(`pages.myPaystubs.runStatus.${status}`)}
    </Badge>
  );
}
